// CarrinhoPage.js — Página do carrinho (/carrinho): a ETAPA 0 do funil de compra.
//
// Estrutura:
//   - Trilha de compra (Carrinho → Dados e revisão → Pagamento)
//   - Lista de itens (capa, título, autor, nota, ações e seletor de quantidade)
//   - Resumo à direita (subtotal, frete, TOTAL e "Finalizar Pedido")
//   - "Salvos para mais tarde" (itens salvos para depois)
//   - "Você poderia gostar" (slider de recomendações)
//
// ============== O QUE MUDOU EM 11/09/2026 (pedido do dono) ==============
//
//  1. SEM TÍTULO DE PÁGINA. O <h1> "Carrinho", com o ícone do carrinho ao
//     lado, repetia o que a URL, o cabeçalho e a própria lista de livros já
//     diziam. No lugar dele entrou a trilha: a primeira coisa da página passou
//     a ser o CAMINHO até o pagamento, e não o rótulo da tela.
//  2. O BOTÃO DE FECHAR O PEDIDO VIROU A PONTE. Era um <Link> que trocava de
//     página no mesmo quadro do clique. Agora ele liga `indo`, a linha laranja
//     corre da bolinha 1 para a 2 e SÓ ENTÃO a navegação acontece — ver
//     TrilhaCompra, que avisa pelo `aoTerminar`.
//  3. A LISTA RESPIRA. A capa tinha 64px de largura: miniatura de gaveta
//     lateral numa página que não mostra mais nada além dela. E "Salvar para
//     mais tarde | Remover" eram dois links de texto separados por uma barra —
//     o desenho do site antigo, que fazia a ação de apagar parecer link de
//     rodapé e não dava área de toque nenhuma no celular.
//  4. "SALVOS PARA MAIS TARDE" GANHOU SUPERFÍCIE. Era uma lista solta com
//     divide-y direto no fundo da página, sem card nenhum, e por isso parecia
//     recortada de outro site.
//  5. O RESUMO SEPARA O QUE SOMA. Antes uma linha só, chamada "Subtotal", já
//     trazia o frete embutido: o número certo com o nome errado. Agora são
//     subtotal e frete em cima, divisória, e o TOTAL como a linha de maior
//     peso — o valor final é exatamente o mesmo de antes.
import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CarrinhoContext } from "../../services/context/CarrinhoContext";
import SliderProdutos from "../produto/sliderProduto/SliderProduto";
import NotaLivro from "../produto/paginaProduto/NotaLivro";
import BorderGlow from "../ui/BorderGlow";
import TrilhaCompra from "../checkout/TrilhaCompra";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

const fmtPreco = (p) => (parseFloat(p) || 0).toFixed(2).replace(".", ",");

// O "de" riscado, quando o item entrou no carrinho com desconto.
//
// O CARRINHO E A TELA QUE EVITA O ABANDONO. O selo no card faz a pessoa clicar;
// aqui, ver que o valor caiu e o que a faz seguir para o pagamento em vez de
// fechar a aba. Mostrar o desconto so na pagina do produto deixa a metade mais
// cara do funil sem o argumento.
//
// `preco_original` vem junto com o item (ver utils/precoLivro.js). Item salvo
// antes desta mudanca nao tem o campo, e a linha simplesmente aparece sem o
// riscado -- nunca com um "de" inventado.
const PrecoDoItem = ({ item, tamanho = "text-xl" }) => {
  const cheio = parseFloat(item?.preco_original);
  const pago = parseFloat(item?.preco) || 0;
  const temDesconto = !isNaN(cheio) && cheio > pago;

  return (
    <p className="text-white whitespace-nowrap">
      {temDesconto && (
        <span className="block text-xs text-white/40 line-through leading-none mb-0.5">
          R$ {fmtPreco(cheio)}
        </span>
      )}
      <span className="text-xs align-top">R$</span>{" "}
      <span className={tamanho + " font-bold"}>{fmtPreco(pago)}</span>
      {temDesconto && (
        <span className="ml-1.5 text-[10px] font-bold uppercase text-insigne-orange align-middle">
          {Math.round((1 - pago / cheio) * 100)}% off
        </span>
      )}
    </p>
  );
};

const autorDe = (item) => {
  const a = item.autores;
  if (Array.isArray(a) && a.length) return a.map((x) => (typeof x === "object" ? x?.nome : x)).join(", ");
  return item.autor || "";
};

// A capa é um POÇO dentro do cartão da lista, e não um cartão em cima de outro:
// fundo rebaixado e borda fina. Dar a ela a sombra dura do `.cartao` faria cada
// linha da lista parecer dois objetos empilhados.
const Capa = ({ item, className }) => (
  <div className={"rounded-card border border-line overflow-hidden bg-insigne-navy-deep shrink-0 flex items-center justify-center " + className}>
    {item.image
      ? <img src={`${API_URL}public/${item.image}`} alt={item.titulo} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
      : <i className="bi bi-book text-white/30 text-2xl" />}
  </div>
);

// As ações de cada linha: "salvar para mais tarde", "remover", "mover para o
// carrinho".
//
// Eram links de texto com uma barrinha `|` no meio. Além de ser o desenho do
// site antigo, um link de 14px é um alvo de 14px: no celular a pessoa erra e
// apaga o livro que queria guardar. Como botão com contorno cada ação tem área
// de toque, ícone e um hover que anuncia o que vai acontecer — e o vermelho
// aparece só em quem apaga.
//
// O hover mora no fundo e no texto, e não na cor do contorno: o `.border` do
// Bootstrap é um shorthand com !important e prende a cor de toda peça que tem
// essa classe na linha do tema (ver "CAUSA-RAIZ" no App.css). Um
// `hover:border-*` aqui nunca acenderia — é o mesmo acordo da Minha Lista.
const AcaoItem = ({ icone, children, perigo = false, onClick, titulo }) => (
  <button
    type="button"
    onClick={onClick}
    title={titulo}
    className={
      "foco-laranja inline-flex items-center gap-1.5 rounded-controle border px-2.5 py-1.5 " +
      "text-xs font-semibold transition-colors " +
      (perigo
        ? "border-line text-white/50 hover:bg-red-500/10 hover:text-red-400"
        : "border-line text-white/70 hover:bg-insigne-orange/10 hover:text-insigne-orange")
    }
  >
    <i className={"bi " + icone} /> {children}
  </button>
);

// A plaquinha de formato. O seletor de quantidade trava o "+" para e-book (não
// se compram duas cópias do mesmo arquivo) e, sem essa etiqueta, o botão
// desabilitado parecia defeito da página.
const SeloFormato = ({ item }) => (
  <span className="selo self-start border border-line bg-white/5 text-white/55">
    <i className={"bi " + (item.formato === "fisico" ? "bi-book" : "bi-tablet")} />
    {item.formato === "fisico" ? "Livro físico" : "E-book"}
  </span>
);

const CarrinhoPage = () => {
  const {
    carrinho, salvos, valorTotal, totalItens, getFreteInfo,
    modificarQuantidade, removerItem, salvarParaDepois, moverParaCarrinho, removerSalvo,
  } = useContext(CarrinhoContext);

  const navigate = useNavigate();
  // `indo` é o estado da ponte: enquanto for true a linha laranja está correndo
  // da etapa 1 para a 2 e o botão fica travado. Sem a trava, dois toques viram
  // duas navegações para o checkout.
  const [indo, setIndo] = useState(false);

  const freteInfo = getFreteInfo() || {};
  const freteGratis = !freteInfo.temLivrosFisicos || (freteInfo.freteTotal || 0) === 0;
  const frete = freteGratis ? 0 : freteInfo.freteTotal || 0;

  const temCarrinho = carrinho.length > 0;
  const vazio = !temCarrinho && salvos.length === 0;

  const aoFinalizar = () => {
    if (!temCarrinho || indo) return;
    // A rolagem sobe ANTES de o traço correr: a trilha fica no alto da página e
    // quem clica no botão está lá embaixo, na coluna do resumo — não veria a
    // linha andar. Era o `onClick` do antigo <Link> e continua aqui.
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIndo(true);
  };

  // Chamava-se `Stepper` até 11/09/2026. Agora existe uma trilha de verdade no
  // funil (TrilhaCompra) e dois "Stepper" na mesma tela — um que conta cópias,
  // outro que conta etapas — só confundem quem for mexer nisto depois.
  const SeletorQtd = ({ item }) => {
    const qtd = item.quantidade || 1;
    const isFisico = item.formato === "fisico";
    return (
      <div className="inline-flex items-center rounded-controle border border-line bg-insigne-navy-deep overflow-hidden">
        <button
          onClick={() => (qtd <= 1 ? removerItem(item.id, item.formato) : modificarQuantidade(item.id, item.formato, qtd - 1))}
          className="foco-laranja w-9 h-9 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5"
          aria-label={qtd <= 1 ? "Remover" : "Diminuir"}
        >
          <i className={"bi " + (qtd <= 1 ? "bi-trash text-red-400" : "bi-dash-lg")} />
        </button>
        <span className="min-w-9 text-center text-sm font-semibold text-white">{qtd}</span>
        <button
          onClick={() => isFisico && modificarQuantidade(item.id, item.formato, qtd + 1)}
          disabled={!isFisico}
          title={isFisico ? "Aumentar" : "E-book tem uma cópia só"}
          className="foco-laranja w-9 h-9 flex items-center justify-center text-insigne-orange hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Aumentar"
        >
          <i className="bi bi-plus-lg" />
        </button>
      </div>
    );
  };

  return (
    <div className="bg-insigne-bg fundo-abertura min-h-screen pt-[var(--nav-h)] px-4 md:px-8 pb-16 text-white">
      <div className="max-w-[1280px] mx-auto">
        {/* A trilha some quando não há carrinho NEM salvos: não existe processo
            a começar, e três etapas por cima de uma tela vazia só cobram o que
            a pessoa não tem como fazer. Ela já é transparente e já põe o texto
            embaixo da bolinha — nunca embrulhar em `bloco` ou `cartao`. */}
        {!vazio && (
          <TrilhaCompra
            atual={0}
            avancando={indo}
            aoTerminar={() => navigate(main + "checkout/")}
            className="max-w-[720px] mx-auto mt-6 mb-8"
          />
        )}

        {vazio ? (
          <div className="bloco max-w-[560px] mx-auto mt-10 px-6 py-12 text-center">
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-line bg-insigne-navy-deep">
              <i className="bi bi-cart-x text-3xl text-white/35" />
            </span>
            <h2 className="titulo-display text-[2rem] mt-6">Seu carrinho está vazio</h2>
            <p className="text-white/50 mt-1.5">Que tal explorar a loja e encontrar algo interessante?</p>
            <Link
              to={main + "livros"}
              className="btn-solido foco-laranja mt-6 inline-flex items-center gap-2 rounded-controle px-5 py-2.5 text-sm font-semibold"
            >
              <i className="bi bi-journals" /> Ver livros
            </Link>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* ===== Coluna esquerda: itens ===== */}
            <div className="flex-1 min-w-0 w-full">
              {temCarrinho ? (
                <div className="cartao divide-y divide-line">
                  {carrinho.map((item) => (
                    <div key={`${item.id}-${item.formato}`} className="flex gap-3.5 sm:gap-5 p-3.5 sm:p-5">
                      {/* 80px no celular e 112px do `sm` para cima: com a capa
                          de 112px já em 400px de largura, o preço e o seletor
                          de quantidade não cabem lado a lado e a linha empurra
                          a página para a rolagem horizontal. */}
                      <Capa item={item} className="w-20 h-28 sm:w-28 sm:h-40" />
                      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:justify-between gap-3.5">
                        <div className="min-w-0 flex flex-col">
                          <SeloFormato item={item} />
                          <h3 className="text-base sm:text-lg font-bold text-white leading-snug mt-1.5">{item.titulo || "Produto sem título"}</h3>
                          {autorDe(item) && <p className="text-white/55 text-sm mt-1">{autorDe(item)}</p>}
                          {/* Mesma guarda da Minha Lista: o carrinho só passou a
                              guardar os campos de avaliação junto com o item em
                              19/08/2026. Para quem já tinha o livro no carrinho
                              antes disso o campo não existe, e escrever "sem
                              avaliações" seria afirmar o que não sabemos. */}
                          {item.avaliacoes_count != null && (
                            <NotaLivro livro={item} size="text-[11px]" compacto className="mt-1.5" />
                          )}
                          {/* `mt-auto` cola as ações no pé da linha: a capa é
                              alta e, sem isso, os dois botões ficariam boiando
                              no meio de um vazio do tamanho dela. */}
                          <div className="flex flex-wrap items-center gap-2 mt-auto pt-3.5">
                            <AcaoItem icone="bi-bookmark" onClick={() => salvarParaDepois(item.id, item.formato)}>
                              Salvar para mais tarde
                            </AcaoItem>
                            <AcaoItem icone="bi-trash" perigo onClick={() => removerItem(item.id, item.formato)}>
                              Remover
                            </AcaoItem>
                          </div>
                        </div>
                        {/* `flex-wrap` só existe para a tela estreita: o preço
                            tem `whitespace-nowrap` e o seletor tem três botões
                            de 36px, então nenhum dos dois encolhe. Sem a quebra,
                            um título com desconto de "% off" estoura o cartão
                            e a PÁGINA inteira ganha rolagem lateral. */}
                        <div className="flex flex-wrap items-center justify-between gap-2.5 sm:flex-col sm:flex-nowrap sm:items-end sm:gap-3.5 shrink-0">
                          <PrecoDoItem item={item} />
                          <SeletorQtd item={item} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bloco px-6 py-10 text-center">
                  <i className="bi bi-cart text-2xl text-white/30" />
                  <p className="text-white/55 mt-2.5">
                    Nenhum item no carrinho. Os livros abaixo estão guardados para depois.
                  </p>
                </div>
              )}

              {/* ===== Salvos para mais tarde ===== */}
              {salvos.length > 0 && (
                <div className="mt-10">
                  <h2 className="flex items-center gap-2.5 text-base font-bold mb-3.5">
                    <i className="bi bi-bookmark text-insigne-orange" /> Salvos para mais tarde
                    <span className="text-white/40 text-sm font-normal">({salvos.length})</span>
                  </h2>
                  {/* Mesma superfície da lista de cima: são livros da mesma
                      pessoa, no mesmo pedido em potencial. A diferença de peso
                      entre as duas fica na capa menor e no preço menor, não em
                      uma ter card e a outra não. */}
                  <div className="cartao divide-y divide-line">
                    {salvos.map((item) => (
                      <div key={`s-${item.id}-${item.formato}`} className="flex gap-3.5 sm:gap-5 p-3.5 sm:p-5">
                        <Capa item={item} className="w-16 h-24" />
                        <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:justify-between gap-3.5">
                          <div className="min-w-0">
                            <h3 className="font-bold text-white leading-snug">{item.titulo}</h3>
                            {autorDe(item) && <p className="text-white/55 text-sm mt-1">{autorDe(item)}</p>}
                            <div className="flex flex-wrap items-center gap-2 mt-3.5">
                              <AcaoItem icone="bi-cart-plus" onClick={() => moverParaCarrinho(item.id, item.formato)}>
                                Mover para o carrinho
                              </AcaoItem>
                              <AcaoItem icone="bi-trash" perigo onClick={() => removerSalvo(item.id, item.formato)}>
                                Remover
                              </AcaoItem>
                            </div>
                          </div>
                          <div className="shrink-0">
                            <PrecoDoItem item={item} tamanho="text-lg" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ===== Coluna direita: resumo ===== */}
            <aside className="w-full lg:w-[360px] shrink-0 lg:sticky lg:top-[150px]">
              {/* Cartão único e parado: o custo do BorderGlow é um `style` por
                  movimento do ponteiro, e aqui ele não se repete por item da
                  lista. `raio={3}` porque a moldura tem de acender no MESMO
                  contorno da superfície que envolve — e esta é `.cartao`, de
                  3px; os que envolvem um `.bloco` (o login, o modal de
                  avaliação) pedem 4. O resumo do CHECKOUT não recebe o mesmo: lá
                  o cartão fica sob o cursor durante toda a rolagem e piscaria
                  enquanto a pessoa confere o que vai pagar.
                  `p-[1.25rem]` e não `p-5`: o Bootstrap declara `.p-5` com
                  !important em 3rem, e ele vence até o `sm:p-6` — o resumo
                  ficava com o dobro do respiro em qualquer largura. */}
              <BorderGlow raio={3} className="cartao p-[1.25rem] sm:p-6">
                <div className="flex items-baseline justify-between gap-2.5">
                  <span className="text-white/70 text-sm">
                    Subtotal ({totalItens} {totalItens === 1 ? "livro" : "livros"})
                  </span>
                  <span className="text-white font-semibold">R$ {fmtPreco(valorTotal)}</span>
                </div>
                <div className="flex items-baseline justify-between gap-2.5 mt-2.5">
                  <span className="text-white/70 text-sm">Frete</span>
                  {freteGratis ? (
                    <span className="text-emerald-400 font-semibold">Grátis</span>
                  ) : (
                    <span className="text-white font-semibold">R$ {fmtPreco(freteInfo.freteTotal)}</span>
                  )}
                </div>

                {/* A divisória é o que faz o TOTAL ser total: sem ela as três
                    linhas têm o mesmo peso e a maior vira apenas "mais uma
                    linha grande". */}
                <div className="border-t border-line my-3.5" />

                <div className="flex items-end justify-between gap-2.5">
                  <span className="text-sm font-bold uppercase tracking-wide text-white/75">Total</span>
                  <span className="text-white leading-none">
                    <span className="text-xs align-top">R$</span>{" "}
                    <span className="text-3xl font-bold">{fmtPreco(valorTotal + frete)}</span>
                  </span>
                </div>

                {freteGratis && (
                  <p className="flex items-center gap-2 text-emerald-400 text-sm font-medium mt-3.5">
                    <i className="bi bi-check-circle-fill" /> Frete Grátis nesse pedido!
                  </p>
                )}
              </BorderGlow>

              <button
                type="button"
                onClick={aoFinalizar}
                disabled={!temCarrinho || indo}
                aria-disabled={!temCarrinho || indo}
                // `py-3` e não `py-3.5`: o Bootstrap declara `.py-3` com !important
                // em 1rem, enquanto `py-3.5` (que ele não tem) cai no Tailwind com
                // 0,875rem. O fracionário deixava o CTA de fechar o pedido mais
                // baixo que os botões comuns da mesma página.
                // `.btn-solido` só entra com o carrinho cheio: a classe traz
                // fundo, borda e a inversão do hover, e pintaria de laranja um
                // botão que não leva a lugar nenhum. Por isso cor e transição
                // vivem em cada ramo, e não na base.
                className={"foco-laranja mt-4 w-full inline-flex items-center justify-center gap-2 rounded-controle px-5 py-3 text-base font-semibold " +
                  (temCarrinho
                    ? "btn-solido"
                    : "bg-white/10 text-white/40 pointer-events-none transition-colors") +
                  (indo ? " pointer-events-none" : "")}
              >
                {/* O RÓTULO NÃO MUDA enquanto a linha corre — só o ícone. Trocar
                    o texto por "Indo..." mudaria a largura do botão bem debaixo
                    do dedo que acabou de tocar nele. */}
                Finalizar Pedido
                <i className={"bi " + (indo ? "bi-arrow-repeat animate-spin" : "bi-arrow-right")} />
              </button>
            </aside>
          </div>
        )}

        {/* ===== Recomendações ===== */}
        {/* `-mx-6`, e não `-mx-4 md:-mx-8`: o respiro lateral da página é
            1,5rem em TODA largura, porque `px-4` é homônimo do Bootstrap e
            vence o `md:px-8` com !important. Com -2rem, este bloco passava 8px
            da borda direita e a página inteira rolava de lado a partir de
            768px (varredura de 18/09/2026). -1,5rem encosta exato. */}
        <div className="mt-8 -mx-6">
          <SliderProdutos nome="Você poderia gostar" icone="bi-stars" random expansivo />
        </div>
      </div>
    </div>
  );
};

export default CarrinhoPage;
